app.service('recordingUploadService', ['$http', 'HttpAuth', 'apiHelper', 'portalHelper', 'CONSTANTS',
  function($http, HttpAuth, apiHelper, portalHelper, CONSTANTS) {

    const API_URL = apiHelper.getApiUrl();
    const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks
    const MAX_RECORDING_FILE_SIZE = CONSTANTS.MAX_RECORDING_FILE_SIZE_BYTES; // 2GB in bytes
    const SUPPORTED_EXTENSIONS = ['mp3', 'mp4', 'wav', 'm4a'];
    const SUPPORTED_MIME_TYPES = ['audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/x-m4a', 'video/mp4'];
    const SUPPORTED_FORMATS = SUPPORTED_MIME_TYPES; // For backward compatibility

    // Validate file before upload
    const validateFile = (file) => {
      const errors = [];

      if (!file) {
        errors.push('No file selected');
        return errors;
      }

      // check MIME type of file
      const isValidMimeType = SUPPORTED_MIME_TYPES.some(format => file.type === format);

      if (!isValidMimeType) {
        errors.push('Supported formats: MP3, MP4, WAV, M4A');
      }

      // Check file size
      if (file.size > MAX_RECORDING_FILE_SIZE) {
        errors.push('File size must be less than 2GB');
      }

      // Check file format
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const isValidFormat = SUPPORTED_MIME_TYPES.some(format => file.type === format) || SUPPORTED_EXTENSIONS.includes(fileExtension);

      if (!isValidFormat) {
        errors.push('Supported formats: ' + SUPPORTED_EXTENSIONS.map(ext => ext.toUpperCase()).join(', '));
      }

      return errors;
    };


    // Initiate multipart upload
    const initiateMultipartUpload = (fusionAssignmentId, file, entityPath = 'fusion_assignment') => {
      const url = `${API_URL}/api/admin/${entityPath}/${fusionAssignmentId}/recordings/initiate-recording-upload/`;
      const totalParts = Math.ceil(file.size / CHUNK_SIZE);

      const payload = {
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        parts: totalParts
      };

      return HttpAuth.post(url, payload);
    };

    // Upload file chunk to S3
    const uploadChunk = (presignedUrl, chunk) => {
      return $http.put(presignedUrl, chunk, {
        headers: {
          'Content-Type': 'application/octet-stream'
        },
        withCredentials: false  // Explicitly disable credentials
      });
    };

    /**
     * Create upload promises for each chunk of the file
     * @param {Array} presignedUrls - Array of presigned URLs for each chunk
     * @param {File} file - The file being uploaded
     * @param {Array} uploadedParts - Array to track uploaded parts
     * @param {Function} progressCallback - Callback for progress updates
     * @returns {Array} Array of upload promises
     */
    function getChunkUploadPromises(presignedUrls, file, uploadedParts, progressCallback) {
      const chunkPromises = [];
      for (let i = 0; i < presignedUrls.length; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const presignedUrl = presignedUrls[i].url;

        const chunkPromise = uploadChunk(presignedUrl, chunk)
          .then(chunkResponse => {
            uploadedParts.push({
              ETag: chunkResponse.headers('ETag').replace(/"/g, ''),
              PartNumber: i + 1
            });

            // Update progress
            const chunkProgress = 10 + (uploadedParts.length / presignedUrls.length) * 80;
            if (progressCallback) {
              progressCallback({
                stage: 'uploading',
                progress: Math.round(chunkProgress),
                chunksUploaded: uploadedParts.length,
                totalChunks: presignedUrls.length
              });
            }
            return chunkResponse;
          });

        chunkPromises.push(chunkPromise);
      }
      return chunkPromises;
    }

    // Complete multipart upload
    const completeMultipartUpload = (fusionAssignmentId, uploadId, key, filename, parts, transcriptData = {}, entityPath = 'fusion_assignment') => {
      const url = `${API_URL}/api/admin/${entityPath}/${fusionAssignmentId}/recordings/complete-recording-upload/`;

      const payload = {
        upload_id: uploadId,
        key: key,
        filename: filename,
        parts: parts,
        transcript_content: transcriptData.transcript_content || '',
        qa_questions: transcriptData.qa_questions || '',
        qa_transcripts: transcriptData.qa_transcripts || '',
        summary: transcriptData.summary || ''
      };

      return HttpAuth.post(url, payload);
    };

    // Main upload function
    const uploadRecording = (fusionAssignmentId, file, progressCallback, existingRecordings = [], transcriptData = {}, entityPath = 'fusion_assignment') => {
      return new Promise((resolve, reject) => {
        // Validate file
        const validationErrors = validateFile(file);
        if (validationErrors.length > 0) {
          reject({ errors: validationErrors });
          return;
        }

        // Check for duplicate names
        const duplicateRecording = existingRecordings.find(recording =>
          recording.recording_name === file.name && recording.active
        );

        if (duplicateRecording) {
          reject({
            isDuplicate: true,
            message: `File with name "${file.name}" already exists.`,
            existingRecording: duplicateRecording
          });
          return;
        }

        let uploadId;
        let key;
        const uploadedParts = [];

        // Step 1: Initiate multipart upload
        initiateMultipartUpload(fusionAssignmentId, file, entityPath)
          .then(response => {
            uploadId = response.data.upload_id;
            key = response.data.key;
            const presignedUrls = response.data.parts;

            if (progressCallback) progressCallback({ stage: 'initiated', progress: 10 });

            // Step 2: Upload chunks
            const chunkPromises = getChunkUploadPromises(presignedUrls, file, uploadedParts, progressCallback);

            return Promise.all(chunkPromises);
          })
          .then(() => {
            // Sort parts by part number
            uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber);

            if (progressCallback) progressCallback({ stage: 'completing', progress: 95 });

            // Step 3: Complete multipart upload
            return completeMultipartUpload(fusionAssignmentId, uploadId, key, file.name, uploadedParts, transcriptData, entityPath);
          })
          .then(response => {
            if (progressCallback) progressCallback({ stage: 'completed', progress: 100 });
            resolve(response.data.recording);
          })
          .catch(error => {
            reject(error);
          });
      });
    };

    // Get recordings list
    const getRecordings = (fusionAssignmentId, includeInactive = false, entityPath = 'fusion_assignment') => {
      const url = `${API_URL}/api/admin/${entityPath}/${fusionAssignmentId}/recordings/`;
      const params = includeInactive ? { include_inactive: true } : {};

      return HttpAuth.get(url, { params });
    };

    // Delete recording (soft delete)
    const deleteRecording = (fusionAssignmentId, recordingId, entityPath = 'fusion_assignment') => {
      const url = `${API_URL}/api/admin/${entityPath}/${fusionAssignmentId}/recordings/${recordingId}/`;
      return HttpAuth.delete(url);
    };

    // Reorder recordings
    const reorderRecordings = (fusionAssignmentId, recordingIds, entityPath = 'fusion_assignment') => {
      const url = `${API_URL}/api/admin/${entityPath}/${fusionAssignmentId}/recordings/reorder/`;
      const payload = { recording_ids: recordingIds };

      return HttpAuth.post(url, payload);
    };


    // Update recording
    const updateRecording = (fusionAssignmentId, recordingId, data, entityPath = 'fusion_assignment') => {
      const url = `${API_URL}/api/admin/${entityPath}/${fusionAssignmentId}/recordings/${recordingId}/`;
      return HttpAuth.patch(url, data);
    };

    // Public API
    return {
      validateFile,
      uploadRecording,
      getRecordings,
      deleteRecording,
      reorderRecordings,
      updateRecording,
      SUPPORTED_FORMATS,
      SUPPORTED_EXTENSIONS,
      SUPPORTED_MIME_TYPES,
      MAX_RECORDING_FILE_SIZE: MAX_RECORDING_FILE_SIZE / (1024 * 1024 * 1024) // Return in GB for display
    };
  }
]);
